import React, { useState, useEffect, useMemo } from 'react';
import { round3 } from '../../utils/money';
import { Link } from 'react-router-dom';
import { IcSearch, IcCreditCard, IcBanknote, IcClose, IcCalendar, IcUser, IcPackage, IcSparkles, IcFileText, IcChevronLeft, IcChevronRight, IcWallet, IcAlertCircle, IcShoppingCart, IcCheckCircle, IcFilters, IcChevronDown, IcChevronUp, IcHandCoins, IcGift, IcRotateCcw, IcUserCheck } from '../../theme/icons';
import salesService from '../../services/salesService';
import VenteTabs from '../../components/VenteTabs';
import Swal from '../../theme/swal';
import { requestIdentity, withIdentity } from '../../services/identityGate';
import { BtnSk, CardSkeleton, Sk } from '../../components/Skeleton';

// The history paginates BY DAY: each "page" is one calendar day. A large page size
// makes sure every sale of the day comes back in a single fetch.
const DAY_PAGE_SIZE = 1000;

// Server-side status filter. « Crédit » = ventes non soldées (reste à payer > 0),
// ce qui couvre à la fois les partielles et les impayées.
const STATUS_TABS = [
  { value: '', label: 'Toutes', icon: null },
  { value: 'paid', label: 'Payées', icon: IcCheckCircle },
  { value: 'credit', label: 'Crédit', icon: IcAlertCircle },
  { value: 'retour', label: 'Retours', icon: IcRotateCcw },
];

const fmtDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const SellerHistorySales = () => {
  // Filters (text inputs update immediately; the search is debounced before fetching)
  const [filters, setFilters] = useState({ clientName: '', productName: '' });
  const [debClientName, setDebClientName] = useState('');
  const [debProductName, setDebProductName] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  // Pagination is by DAY — this is the day currently shown (defaults to today).
  const [currentDay, setCurrentDay] = useState(fmtDateInput(new Date()));

  // Data
  const [sales, setSales] = useState([]);
  const [creditPayments, setCreditPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ ca: 0, encaisse: 0, credit_paye: 0, credit: 0, count: 0, services_count: 0, services_total: 0 });

  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentSale, setCurrentSale] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [saleDetail, setSaleDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Debounce client / product search shortly after typing stops.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebClientName(filters.clientName.trim());
      setDebProductName(filters.productName.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [filters.clientName, filters.productName]);

  // The date range is the single selected day (start = end = currentDay).
  const filterArgs = useMemo(() => ({
    clientName: debClientName,
    productName: debProductName,
    status: statusFilter,
    startDate: currentDay,
    endDate: currentDay,
  }), [debClientName, debProductName, statusFilter, currentDay]);

  // Single fetch whenever the day or any (debounced) filter changes.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const pageRes = await salesService.getSalesPage({ ...filterArgs, page: 1, pageSize: DAY_PAGE_SIZE });
        if (!active) return;
        setSales(pageRes.results);
        setTotalCount(pageRes.count);
      } catch (err) {
        if (!active) return;
        console.error('Error fetching sales:', err);
        setError('Erreur lors du chargement des ventes. Veuillez réessayer.');
        setSales([]);
        setTotalCount(0);
      } finally {
        if (active) setLoading(false);
      }
      // KPI aggregates for the selected day.
      salesService.getSalesSummary(filterArgs).then((s) => { if (active) setSummary(s); });
      // Encaissements crédit reçus ce jour-là (lignes dédiées du tableau).
      salesService.getCreditPayments(filterArgs).then((rows) => { if (active) setCreditPayments(rows); });
    })();
    return () => { active = false; };
  }, [filterArgs]);

  const refreshCurrent = async () => {
    try {
      const pageRes = await salesService.getSalesPage({ ...filterArgs, page: 1, pageSize: DAY_PAGE_SIZE });
      setSales(pageRes.results);
      setTotalCount(pageRes.count);
    } catch (err) { console.error(err); }
    salesService.getSalesSummary(filterArgs).then(setSummary);
    salesService.getCreditPayments(filterArgs).then(setCreditPayments);
  };

  // ---- Day navigation ----
  const isToday = currentDay === fmtDateInput(new Date());
  const shiftDay = (delta) => {
    const d = new Date(currentDay + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setCurrentDay(fmtDateInput(d));
  };
  const dayLabel = (() => {
    try {
      return new Date(currentDay + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    } catch (e) { return currentDay; }
  })();

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const changeStatus = (value) => { setStatusFilter(value); };

  const hasActiveFilters = filters.clientName || filters.productName || statusFilter;
  const panelFilterCount =
    (filters.clientName ? 1 : 0) + (filters.productName ? 1 : 0) + (statusFilter ? 1 : 0);

  const clearFilters = () => {
    setFilters({ clientName: '', productName: '' });
    setDebClientName('');
    setDebProductName('');
    setStatusFilter('');
  };

  // ---- Payment ----
  const handlePayment = (sale) => {
    setCurrentSale(sale);
    setPaymentAmount(getAmountDue(sale).toFixed(3));
    setShowPaymentModal(true);
  };

  const submitPayment = async () => {
    if (!currentSale || !paymentAmount) return;
    try {
      setProcessingPayment(true);
      const amount = round3(parseFloat(paymentAmount));
      if (isNaN(amount) || amount <= 0) {
        Swal.fire('Erreur', 'Veuillez entrer un montant valide', 'error');
        return;
      }
      if (amount > getAmountDue(currentSale) + 0.0005) {
        Swal.fire('Erreur', 'Le montant ne peut pas dépasser le montant dû', 'error');
        return;
      }
      // Qui encaisse ce crédit ? (code sur le compte vendeur)
      const who = await requestIdentity({ title: 'Qui encaisse ?' });
      if (who === false) return;
      await withIdentity(who, () => salesService.updatePayment(currentSale.id, { payment_amount: amount }));
      setShowPaymentModal(false);
      setCurrentSale(null);
      setPaymentAmount('');
      await refreshCurrent();
      Swal.fire('Succès', 'Paiement enregistré avec succès', 'success');
    } catch (err) {
      console.error('Error processing payment:', err);
      Swal.fire('Erreur', 'Une erreur est survenue lors du paiement', 'error');
    } finally {
      setProcessingPayment(false);
    }
  };

  // ---- Helpers ----
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try { return new Date(dateString).toLocaleDateString('fr-FR'); } catch (err) { return dateString; }
  };
  // Heure de la vente (HH:MM) — sous le nom du vendeur (owner 2026-10-14).
  const formatTime = (dateString) => {
    if (!dateString) return '';
    try { return new Date(dateString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch (err) { return ''; }
  };
  const getTotalPrice = (sale) => sale.total_price || sale.grand_total || 0;
  const getAmountPaid = (sale) => sale.amount_paid || sale.amount_payed || 0;
  const getAmountDue = (sale) => {
    if (sale.amount_due !== undefined) return sale.amount_due;
    if (sale.credit !== undefined) return sale.credit;
    return Math.max(0, getTotalPrice(sale) - getAmountPaid(sale));
  };
  const getCreditPaye = (sale) => sale.credit_paye || 0;
  // Points dépensés sur cette vente (soin payé au coût Avantages et/ou tender
  // gold 1 pt = 1 DT). 0 pour les ventes d'avant l'enregistrement du champ.
  const getPointsUsed = (sale) => parseFloat(sale.points_used) || 0;
  const getPaymentStatus = (sale) => {
    // Retour client (vente négative) : on dit ce qui a été rendu, pas « Payé ».
    if (sale.is_return) {
      const cash = Math.abs(parseFloat(sale.amount_payed) || 0);
      const offset = parseFloat(sale.return_credit_offset) || 0;
      const parts = [];
      if (cash > 0.0005 || offset <= 0) parts.push(`rendu ${cash.toFixed(3)} DT`);
      if (offset > 0) parts.push(`crédit −${offset.toFixed(3)} DT`);
      return { status: `Retour · ${parts.join(' · ')}`, variant: 'bdg-warn' };
    }
    const amountPaid = getAmountPaid(sale);
    const amountDue = getAmountDue(sale);
    // Une vente soldée après un remboursement de crédit est distinguée d'une
    // vente encaissée directement.
    // Variants = grammaire Étiquettes Ki Para (index.css, Etiquettes-2 §2) —
    // famille paiement. Libellé seul depuis le 2026-09-06 (plus d'icône) ;
    // le montant restant reste TOUJOURS dans le badge.
    if (amountDue <= 0) {
      if (getCreditPaye(sale) > 0) return { status: 'Crédit payé', variant: 'bdg-pos3' };
      // Rien encaissé en espèces mais des points dépensés → la vente a été
      // réglée en points ; le badge le dit explicitement.
      if (getPointsUsed(sale) > 0 && amountPaid <= 0.0005) return { status: 'Payé avec points', variant: 'bdg-pos' };
      return { status: 'Payé', variant: 'bdg-pos' };
    }
    // Partiel = teinte rouge #FEE2E2 / #DC2626 (livre §04, facture « ◐ Partiel »).
    if (amountPaid > 0) return { status: `Partiel · reste ${Number(amountDue).toFixed(3)} DT`, variant: 'bdg-alert' };
    return { status: `Non payé · Crédit ${Number(amountDue).toFixed(3)} DT`, variant: 'bdg-critical' };
  };
  // Étiquette de paiement SANS icône (owner 2026-09-06) : libellé seul, comme
  // la version mobile (MobileSellerHistory) qui n'en a jamais eu. L'icône est
  // retirée du composant ET les champs Icon de getPaymentStatus avec, pour ne
  // pas laisser de données mortes.
  const PaymentBadge = ({ sale }) => {
    const ps = getPaymentStatus(sale);
    return <span className={`bdg ${ps.variant}`}>{ps.status}</span>;
  };
  const clientLabel = (c) =>
    c?.name || `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || 'Client de passage';

  // Pagination
  // The history is shown BY PRODUCT (not by client): flatten every sale into one row
  // per product line (services included so nothing is lost). Clicking any row opens
  // the same "Détails de la vente" modal for the parent sale. Credit repayments
  // received that day (acompte client ou bouton « Payer ») get their own teal rows,
  // merged chronologically — hidden when a product search or a status tab is
  // active, since those filters describe sales, not payments.
  const showPaymentRows = !debProductName && !statusFilter;
  const itemRows = useMemo(() => {
    const rows = [];
    for (const sale of sales) {
      const dt = sale.date_created || sale.date_added;
      (sale.details || []).forEach((d, i) => {
        rows.push({
          key: `${sale.id}-p${i}`, sale, type: 'product', dt,
          name: d.product_name || 'Produit',
          qty: d.quantity || 1,
          price: d.price || 0,
          total: d.total_detail != null ? d.total_detail : ((d.price || 0) * (d.quantity || 1)),
        });
      });
      (sale.service_details || []).forEach((s, i) => {
        const price = parseFloat(s.price) || 0;
        const remise = parseFloat(s.remise) || 0;
        const fp = remise > 0 ? price * (1 - remise / 100) : price;
        rows.push({
          key: `${sale.id}-s${i}`, sale, type: 'service', dt,
          name: s.service_name || s.service?.name || 'Service',
          qty: s.quantity || 1,
          price: fp,
          total: s.total_detail != null ? s.total_detail : (fp * (s.quantity || 1)),
        });
      });
    }
    if (showPaymentRows) {
      for (const p of creditPayments) {
        rows.push({ key: `cp-${p.id}`, type: 'payment', dt: p.date, payment: p });
      }
    }
    // Newest first; sort is stable so a sale's product/service lines stay grouped.
    return rows.sort((a, b) => new Date(b.dt) - new Date(a.dt));
  }, [sales, creditPayments, showPaymentRows]);
  const paymentRowCount = showPaymentRows ? creditPayments.length : 0;

  const handleViewSaleDetails = async (sale) => {
    try {
      setLoadingDetail(true);
      setShowDetailModal(true);
      setSaleDetail(null);
      const detailedSale = await salesService.getSaleById(sale.id);
      setSaleDetail(detailedSale);
    } catch (err) {
      console.error('Error fetching sale details:', err);
      setShowDetailModal(false);
      Swal.fire('Erreur', 'Impossible de charger les détails de la vente', 'error');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Abrège les diagnostics dans la sous-ligne Services : « Diagnostic
  // Cheveux » → « d.c », « DIAGNOSTIC VISAGE » → « d.v ». Les autres noms
  // (soins, POIDS…) restent tels quels.
  const abbrevService = (name) => {
    const words = (name || '').trim().split(/\s+/);
    if (words.length > 1 && /^diagnostic$/i.test(words[0])) {
      return 'd.' + words.slice(1).map((w) => w[0].toLowerCase()).join('.');
    }
    return name;
  };

  // CA et Encaissé incluent le crédit payé (remboursements reçus ce jour) — le
  // backend évite le double compte pour les ventes du jour remboursées le jour même.
  // Deux lignes de 3 (ordre owner Studio 2026-09-07, remplace le 2026-08-16) :
  // Ventes · CA · Encaissé / Services · Crédit (reste à payer) · Crédit payé.
  // `valueClass` teinte LA VALEUR seule — même grammaire que le tableau de bord
  // et /statistiques/ventes : vert 900 le CA, vert 700 l'encaissé, rouge 700 le
  // crédit qui reste dû, or 700 le crédit récupéré. « Services » et « Ventes »
  // gardent le gris de .kpi-value (hors sélection owner).
  const KPIS = [
    // Les retours clients ne sont pas des ventes : compte du résumé (hors
    // retours) ; leur montant est déjà déduit du CA / de l'encaissé.
    { key: 'count', label: 'Ventes', value: `${summary.count ?? totalCount}`, icon: IcShoppingCart, tint: 'bg-gray-100 text-gray-600',
      sub: (summary.returns_count || 0) > 0 ? `dont ${summary.returns_count} retour${summary.returns_count > 1 ? 's' : ''} · −${(summary.returns_total || 0).toFixed(3)} DT` : undefined },
    // Owner 2026-09-10 : CA et Encaissé ont TOUJOURS une sous-ligne (comme
    // « Services ») pour que la rangée 1 ait la hauteur de la rangée 2 ;
    // sans données elle alerte « Aucun chiffre d'affaires » / « Aucun
    // encaissement », sinon le détail crédit payé, ou une ligne réservée
    // (espace insécable) qui garde la hauteur.
    {
      key: 'ca', label: "Chiffre d'affaires", value: `${summary.ca.toFixed(3)} DT`,
      valueClass: 'text-green-900',
      sub: (summary.ca || 0) <= 0
        ? "Aucun chiffre d'affaires"
        : (summary.credit_paye_ancien || 0) > 0 ? `dont crédit payé ${(summary.credit_paye_ancien || 0).toFixed(3)}` : '\u00a0',
      icon: IcBanknote, tint: 'bg-blue-50 text-blue-600',
    },
    {
      key: 'encaisse', label: 'Encaissé', value: `${summary.encaisse.toFixed(3)} DT`,
      valueClass: 'text-green-700',
      sub: (summary.encaisse || 0) <= 0
        ? 'Aucun encaissement'
        : (summary.credit_paye || 0) > 0 ? `dont crédit payé ${(summary.credit_paye || 0).toFixed(3)}` : '\u00a0',
      icon: IcWallet, tint: 'bg-emerald-50 text-emerald-600',
    },
    {
      key: 'services', label: 'Services',
      value: `${(summary.services_total || 0).toFixed(3)} DT`,
      // Détail par service vendu, soins en tête (« 1 Soin Basique · 1 d.c ») —
      // la div tronque, le title affiche la liste complète.
      sub: (summary.services_detail || []).length
        ? summary.services_detail.map((d) => `${d.count} ${abbrevService(d.name)}`).join(' · ')
        : 'Aucun service',
      icon: IcSparkles, tint: 'bg-green-50 text-green-700', // Studio 2026-08-16
    },
    { key: 'credit', label: 'Crédit (reste à payer)', value: `${summary.credit.toFixed(3)} DT`, valueClass: 'text-red-700', icon: IcAlertCircle, tint: 'bg-red-50 text-red-600' },
    { key: 'credit_paye', label: 'Crédit payé', value: `${(summary.credit_paye || 0).toFixed(3)} DT`, valueClass: 'text-amber-700', icon: IcHandCoins, tint: 'bg-teal-50 text-teal-600' },
  ];

  return (
    // Same wrapper as the other Ventes pages (owner request): full width,
    // px-4 py-5, default background — no gray overlay, no centered max-w.
    <div className="w-full px-4 pt-2 pb-4">
      <div>
        <VenteTabs />
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4">{error}</div>
        )}

        {/* Toolbar row under the page tabs — Filtres button placed like on the
            seller-sales page (owner request), right-aligned. */}
        <div className="flex gap-3 mb-3 justify-end items-center">
          {/* Composants-2 §3 : hors formulaire, Réinitialiser = lien rouge */}
          {hasActiveFilters && (
            <button type="button" onClick={clearFilters} className="link-reset gap-1.5 px-3 py-1.5 text-sm">
              Réinitialiser
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowFilterPanel((v) => !v)}
            className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
          >
            <IcFilters className="text-sm" /> Filtres
            {panelFilterCount > 0 && (
              <span className="bdg-count static ml-2">{panelFilterCount}</span>
            )}
          </button>
        </div>

        {/* Filter panel — carte de filtres Brand Kit §05 (rayon 6, padding 12, gap 9). */}
        {showFilterPanel && (
          <div className="flt-card rounded-2xl p-4 mb-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {STATUS_TABS.map((s) => {
                const active = statusFilter === s.value;
                return (
                  <button
                    key={s.value || 'all'} onClick={() => changeStatus(s.value)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors ${
                      active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {s.icon && <s.icon className="text-xs" />} {s.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="flt-label" htmlFor="sh-filter-client">Client</label>
                <div className="relative">
                  <IcUser size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="sh-filter-client"
                    type="text" name="clientName" value={filters.clientName} onChange={handleFilterChange}
                    placeholder="Rechercher un client (nom)…"
                    className="inp w-full pl-9 pr-3 py-2.5 text-gray-900"
                  />
                </div>
              </div>
              <div>
                <label className="flt-label" htmlFor="sh-filter-product">Produit</label>
                <div className="relative">
                  <IcPackage size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="sh-filter-product"
                    type="text" name="productName" value={filters.productName} onChange={handleFilterChange}
                    placeholder="Rechercher un produit…"
                    className="inp w-full pl-9 pr-3 py-2.5 text-gray-900"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hauteur libre : la page défile normalement comme « Historique des
            actions » (owner 2026-10-05) — plus de bloc .kp-fill à hauteur fixe
            (règle du 2026-09-07) qui coupait la carte et la faisait défiler
            à l'intérieur sous les KPI. */}
        <div className="flex flex-col min-w-0">
        {/* Navigation par jour — kit « Ghost — abandon » (.btn-g) depuis le
            2026-09-06 : fond transparent, texte #4B5563, sans bordure. Les deux
            rangées de la page (ici et sous le tableau) portent le même kit.
            Les chevrons, retirés un temps le 2026-09-06, ont été remis à la
            demande de l'owner. */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <button type="button" onClick={() => shiftDay(-1)} className="btn-g gap-1 px-3 py-2 text-sm"><IcChevronLeft /> Jour précédent</button>
          <span className="text-sm font-semibold text-gray-900 capitalize text-center flex-1">{dayLabel}</span>
          <button type="button" onClick={() => shiftDay(1)} disabled={isToday} className="btn-g gap-1 px-3 py-2 text-sm">Jour suivant <IcChevronRight /></button>
        </div>

        {/* KPI summary bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
          {KPIS.map((k) => (
            /* Même grammaire que les 4 cases de la carte « Ventes / jour » du
               tableau de bord (owner 2026-09-07) : kit .kpi, icône NEUTRE de
               11px en haut à droite (.kpi-icon) au lieu d'une pastille 40px
               teintée, libellé en capitales espacées, valeur tabulaire. */
            <div key={k.key} className="kpi min-w-0 rounded-2xl p-3">
              <k.icon size={11} className="kpi-icon w-[11px] h-[11px]" />
              <div className="kpi-label uppercase pr-6 text-xs mb-1 truncate">{k.label}</div>
              <div className={`kpi-value text-base sm:text-lg truncate ${k.valueClass || ''}`}>{k.value}</div>
              {k.sub && <div className="kpi-sub truncate" title={k.sub}>{k.sub}</div>}
            </div>
          ))}
        </div>

        {/* Sales list — status tabs and filters live in the card header. */}
        {/* Défilement normal de la page (comme Historique des actions), plus de
            défilement interne sous les KPI — demande d'Ahmed 2026-10-05. */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 space-y-3">
            {/* « Produits vendus · <date> » title removed (owner Studio 2026-08-13);
                the counts pill stays right-aligned. */}
            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="flex items-center gap-3">
                {loading && <Sk w={40} h={10} className="sk-inline" />}
                <span className="bdg bdg-pill">
                  {itemRows.length - paymentRowCount} produit{itemRows.length - paymentRowCount !== 1 ? 's' : ''} · {totalCount} vente{totalCount !== 1 ? 's' : ''}
                  {paymentRowCount > 0 && ` · ${paymentRowCount} encaissement${paymentRowCount !== 1 ? 's' : ''}`}
                </span>
              </div>
            </div>

            {/* In-card day navigation removed — replaced by the standalone row
                above the card (owner Studio 2026-08-13). */}
          </div>

          {loading && sales.length === 0 && itemRows.length === 0 ? (
            <div className="text-center py-12">
              <CardSkeleton />
            </div>
          ) : sales.length === 0 && itemRows.length === 0 ? (
            /* État vide (Brand Kit §05) : cadre pointillé, icône 22px, phrase
               13px 600, sous-phrase 11.5px, action encre 36px */
            <div className="empty-ki m-4 py-14">
              <IcSearch size={28} className="empty-ki-icon w-[28px] h-[28px] mb-2" />
              <p className="empty-ki-title text-sm mt-0">Aucune vente trouvée</p>
              <p className="empty-ki-sub">Essayez un autre jour ou retirez les filtres.</p>
              {/* Le raccourci « Nouvelle vente » de l'état vide est retiré (owner
                  2026-09-06) : retrait par le JSX, pas par display:none — .btn-ink
                  sert ailleurs dans l'app. L'onglet « Nouvelle vente » de la barre
                  du haut reste le chemin vers la caisse. */}
              {hasActiveFilters && (
                <button type="button" onClick={clearFilters} className="btn-db mt-4 py-2 px-4 text-sm gap-2">
                  <IcClose /> Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Tableau dense (Brand Kit §05) : en-tête th-ki #e9e9ea 8/12px,
                  lignes 13px 8/12px, séparateurs gray-100, survol #F0FDF4 */}
              <table className="min-w-full divide-y divide-gray-100">
                <thead>
                  <tr>
                    {['Date', 'Produit', 'Qté', 'Total', 'Client', 'Vendeur', 'Statut'].map((h) => (
                      <th key={h} className="th-ki px-4 py-3 text-left text-xs uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {itemRows.map((row) => {
                    if (row.type === 'payment') {
                      const p = row.payment;
                      const time = (() => {
                        try { return new Date(p.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; }
                      })();
                      return (
                        <tr
                          key={row.key}
                          onClick={() => { if (p.sale_id) handleViewSaleDetails({ id: p.sale_id }); }}
                          className={`bg-green-50/40 transition ${p.sale_id ? 'hover:bg-green-50 cursor-pointer' : ''}`}
                        >
                          <td className="td-ki px-4 py-3.5 whitespace-nowrap text-sm text-gray-600">{formatDate(p.date)}</td>
                          <td className="td-ki px-4 py-3.5">
                            <div className="flex items-center gap-2 text-sm font-semibold text-green-700">
                              <IcHandCoins className="text-green-600 shrink-0" size={12} />
                              <span>Encaissement crédit</span>
                            </div>
                            <div className="text-xs text-gray-400">{p.note || 'Acompte'}{time ? ` · ${time}` : ''}</div>
                          </td>
                          <td className="td-ki px-4 py-3.5 whitespace-nowrap text-sm text-gray-400">—</td>
                          <td className="td-ki px-4 py-3.5 whitespace-nowrap text-sm font-semibold text-green-700 tabular-nums">+{(p.amount || 0).toFixed(3)} DT</td>
                          <td className="td-ki px-4 py-3.5 whitespace-nowrap">
                            <div className="text-sm text-gray-700">{p.client?.name || 'Client'}</div>
                            {p.client?.phone && <div className="text-xs text-gray-400">{p.client.phone}</div>}
                          </td>
                          <td className="td-ki px-4 py-3.5 whitespace-nowrap text-sm text-gray-400">—</td>
                          <td className="td-ki px-4 py-3.5 whitespace-nowrap">
                            <span className="bdg bdg-pos3"><IcRotateCcw size={12} /> Crédit payé</span>
                          </td>
                        </tr>
                      );
                    }
                    const sale = row.sale;
                    const due = getAmountDue(sale);
                    return (
                      <tr key={row.key} onClick={() => handleViewSaleDetails(sale)} className="tr-ki transition">
                        <td className="td-ki px-4 py-3.5 whitespace-nowrap text-sm text-gray-600">{formatDate(sale.date_created)}</td>
                        <td className="td-ki px-4 py-3.5">
                          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                            {row.type === 'product'
                              ? <IcPackage className="text-blue-500 shrink-0" size={11} />
                              : <IcSparkles className="text-emerald-500 shrink-0" size={11} />}
                            <span className="truncate max-w-[240px]">{row.name}</span>
                          </div>
                          <div className="text-xs text-gray-400">{row.price.toFixed(3)} DT / unité</div>
                        </td>
                        <td className="td-ki px-4 py-3.5 whitespace-nowrap text-sm text-gray-600 tabular-nums">×{Math.abs(row.qty)}</td>
                        <td className={`td-ki px-4 py-3.5 whitespace-nowrap text-sm font-semibold tabular-nums ${sale.is_return ? 'text-red-700' : 'text-gray-900'}`}>{row.total.toFixed(3)} DT</td>
                        <td className="td-ki px-4 py-3.5 whitespace-nowrap">
                          <div className="text-sm text-gray-700">{clientLabel(sale.client)}</div>
                          {sale.client?.phone && <div className="text-xs text-gray-400">{sale.client.phone}</div>}
                        </td>
                        {/* Qui a encaissé, et à quelle heure. Owner 2026-10-14 : le nom
                            et l'heure seulement — plus de « Code » / « Compte connecté ». */}
                        <td className="td-ki px-4 py-3.5 whitespace-nowrap">
                          <div className={`text-sm ${sale.seller_name ? 'text-gray-700' : 'text-gray-400'}`}>{sale.seller_name || '—'}</div>
                          {formatTime(sale.date_created) && <div className="text-xs text-gray-400 tabular-nums">{formatTime(sale.date_created)}</div>}
                        </td>
                        <td className="td-ki px-4 py-3.5 whitespace-nowrap">
                          {/* Bouton « Payer » de la rangée retiré (owner 2026-09-06) :
                              retrait par le JSX, pas par display:none. L'encaissement
                              reste accessible par la modale de détail de la vente
                              (clic sur la rangée → « Encaisser »). */}
                          <div className="flex items-center gap-2">
                            <PaymentBadge sale={sale} />
                          </div>
                          {/* Détail du paiement de la vente parente */}
                          {due > 0 && (
                            <div className="text-xs text-gray-400 mt-1">
                              Payé {getAmountPaid(sale).toFixed(3)} · Reste <span className="text-red-500 font-medium">{due.toFixed(3)}</span>
                            </div>
                          )}
                          {getCreditPaye(sale) > 0 && (
                            <div className="text-xs text-teal-600 mt-0.5 inline-flex items-center gap-1">
                              <IcHandCoins size={10} /> Crédit payé {getCreditPaye(sale).toFixed(3)} DT
                            </div>
                          )}
                          {getPointsUsed(sale) > 0 && (
                            <div className="text-xs text-green-700 mt-0.5 inline-flex items-center gap-1 font-medium">
                              <IcGift size={10} /> {round3(getPointsUsed(sale))} points utilisés
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {/* Day navigation (one day per page) */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 gap-3">
                <button
                  type="button"
                  className="btn-g gap-1.5 px-3 py-2 text-sm"
                  onClick={() => shiftDay(-1)} disabled={loading}
                >
                  <IcChevronLeft className="w-3 h-3" /> Jour précédent
                </button>
                <span className="text-sm font-semibold text-gray-900 capitalize text-center flex-1">{dayLabel}</span>
                <button
                  type="button"
                  className="btn-g gap-1.5 px-3 py-2 text-sm"
                  onClick={() => shiftDay(1)} disabled={isToday || loading}
                >
                  Jour suivant <IcChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && currentSale && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full">
            <div className="modal-head -mx-6 -mt-6 mb-4 px-6 rounded-t-2xl">
              <h3 className="modal-title text-lg">Effectuer un Paiement</h3>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-1 text-sm text-gray-600">
              <p>Client : <span className="font-medium text-gray-800">{clientLabel(currentSale.client)}</span></p>
              <p>Total : <span className="font-medium text-gray-800">{getTotalPrice(currentSale).toFixed(3)} DT</span></p>
              <p>Reste à payer : <span className="font-medium text-red-600">{getAmountDue(currentSale).toFixed(3)} DT</span></p>
            </div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Montant à payer</label>
            <input
              type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)}
              min="0" max={getAmountDue(currentSale)} step="0.001" placeholder="0.000"
              className="inp w-full px-3 py-2.5 rounded-xl text-gray-900"
            />
            {/* Pied de modale (Brand Kit §05) : filet gray-100, gap 8 */}
            <div className="flex justify-end gap-3 mt-5 -mx-6 px-6 py-[11px] border-t border-gray-100 -mb-6">
              <button onClick={() => setShowPaymentModal(false)} disabled={processingPayment}
                className="btn-g flex-1 px-4 py-2.5">Annuler</button>
              <button onClick={submitPayment} disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0}
                className="btn-p flex-1 px-4 py-2.5 gap-2">
                {processingPayment ? (<><BtnSk /> Traitement…</>) : (<><IcCreditCard /> Confirmer</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sale Detail Modal */}
      {showDetailModal && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header (pinned) — Composants-2 §10 : aplat encre, titre blanc, ✕ gris → blanc */}
            <div className="modal-head items-start gap-3 shrink-0 p-4">
              <div className="min-w-0">
                <h3 className="modal-title text-xl">Détails de la Vente {saleDetail ? `#${saleDetail.id}` : ''}</h3>
                {saleDetail && (
                  <div className="text-sm text-gray-300 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="inline-flex items-center gap-1.5"><IcUser className="text-xs text-gray-400" /> {clientLabel(saleDetail.client)}{saleDetail.client?.phone ? ` · ${saleDetail.client.phone}` : ''}</span>
                    <span className="inline-flex items-center gap-1.5"><IcCalendar className="text-xs text-gray-400" /> {formatDate(saleDetail.date_created)}</span>
                    {saleDetail.seller_name && (
                      <span className="inline-flex items-center gap-1.5"><IcUserCheck className="text-xs text-gray-400" /> Vendu par {saleDetail.seller_name}{formatTime(saleDetail.date_created) ? ` · ${formatTime(saleDetail.date_created)}` : ''}</span>
                    )}
                  </div>
                )}
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white shrink-0" aria-label="Fermer"><IcClose className="text-xl" /></button>
            </div>

            {loadingDetail || !saleDetail ? (
              <div className="p-10 text-center">
                <CardSkeleton />
              </div>
            ) : (
              <>
                {/* Body (scrolls) — vente details first, then paiement */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Products */}
                  {saleDetail.details?.length > 0 && (
                    <div className="bg-blue-50/60 p-4 rounded-xl border border-blue-100">
                      <div className="flex items-center mb-3"><IcPackage className="text-blue-600 mr-2" /><h4 className="font-medium text-gray-800">Produits ({saleDetail.details.length})</h4></div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 bg-white rounded-xl overflow-hidden">
                          <thead><tr className="bg-gray-100">
                            {['Produit', 'Prix', 'Quantité', 'Total'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}
                          </tr></thead>
                          <tbody className="divide-y divide-gray-100">
                            {saleDetail.details.map((d, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm font-medium text-gray-800">{d.product_name || 'N/A'}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{(d.price || 0).toFixed(3)} DT</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{d.quantity || 1}</td>
                                <td className="px-4 py-2 text-sm font-bold text-gray-800">{(d.total_detail || (d.price * d.quantity) || 0).toFixed(3)} DT</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Services */}
                  {saleDetail.service_details?.length > 0 && (
                    <div className="bg-emerald-50/60 p-4 rounded-xl border border-emerald-100">
                      <div className="flex items-center mb-3"><IcSparkles className="text-emerald-600 mr-2" /><h4 className="font-medium text-gray-800">Services ({saleDetail.service_details.length})</h4></div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 bg-white rounded-xl overflow-hidden">
                          <thead><tr className="bg-gray-100">
                            {['Service', 'Prix', 'Remise', 'Quantité', 'Total'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}
                          </tr></thead>
                          <tbody className="divide-y divide-gray-100">
                            {saleDetail.service_details.map((s, i) => {
                              const price = parseFloat(s.price) || 0;
                              const remise = parseFloat(s.remise) || 0;
                              const finalPrice = remise > 0 ? price * (1 - remise / 100) : price;
                              return (
                                <tr key={i} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 text-sm font-medium text-gray-800">{s.service_name || s.service?.name || 'N/A'}</td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {remise > 0 ? (
                                      <div><div className="font-semibold text-emerald-700">{finalPrice.toFixed(3)} DT</div><div className="text-xs text-gray-400 line-through">{price.toFixed(3)} DT</div></div>
                                    ) : <span>{price.toFixed(3)} DT</span>}
                                  </td>
                                  <td className="px-4 py-2 text-sm">
                                    {remise > 0 ? (
                                      <span className="bdg bdg-remise">−{remise}%</span>
                                    ) : <span className="text-gray-300">—</span>}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">{s.quantity || 1}</td>
                                  <td className="px-4 py-2 text-sm font-bold text-emerald-700">{(s.total_detail || (finalPrice * (s.quantity || 1))).toFixed(3)} DT</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {!saleDetail.details?.length && !saleDetail.service_details?.length && (
                    <div className="text-center text-gray-400 text-sm py-6 border border-dashed border-gray-200 rounded-xl">
                      Aucun article dans cette vente.
                    </div>
                  )}

                  {/* Payment summary */}
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <div className="flex items-center mb-3"><IcCreditCard className="text-gray-600 mr-2" /><h4 className="font-medium text-gray-800">Paiement</h4></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="space-y-1.5">
                        <p><span className="font-semibold text-gray-700">Sous-total : </span>{(saleDetail.sub_total || 0).toFixed(3)} DT</p>
                        {Math.abs(saleDetail.discount_amount || 0) > 0 && (
                          <p><span className="font-semibold text-gray-700">Remise : </span>{Math.abs(saleDetail.discount_amount || 0).toFixed(3)} DT{saleDetail.discount_percentage > 0 && ` (${saleDetail.discount_percentage}%)`}</p>
                        )}
                        <p className="font-medium text-gray-800"><span className="font-semibold">{saleDetail.is_return ? 'Total remboursé : ' : 'Total : '}</span>{(saleDetail.is_return ? Math.abs(getTotalPrice(saleDetail)) : getTotalPrice(saleDetail)).toFixed(3)} DT</p>
                      </div>
                      <div className="space-y-1.5">
                        {saleDetail.is_return ? (<>
                          <p><span className="font-semibold text-gray-700">Rendu en espèces : </span>{Math.abs(getAmountPaid(saleDetail)).toFixed(3)} DT</p>
                          {(parseFloat(saleDetail.return_credit_offset) || 0) > 0 && (
                            <p><span className="font-semibold text-gray-700">Déduit du crédit : </span>{(parseFloat(saleDetail.return_credit_offset) || 0).toFixed(3)} DT</p>
                          )}
                        </>) : (
                          <p><span className="font-semibold text-gray-700">Montant payé : </span>{getAmountPaid(saleDetail).toFixed(3)} DT</p>
                        )}
                        {getPointsUsed(saleDetail) > 0 && (
                          <p className="text-green-700"><span className="font-semibold">Payé avec points : </span>{round3(getPointsUsed(saleDetail))} points</p>
                        )}
                        {getCreditPaye(saleDetail) > 0 && (
                          <p className="text-teal-600"><span className="font-semibold">dont crédit payé : </span>{getCreditPaye(saleDetail).toFixed(3)} DT</p>
                        )}
                        {getAmountDue(saleDetail) > 0 && <p className="text-red-600"><span className="font-semibold">Reste à payer : </span>{getAmountDue(saleDetail).toFixed(3)} DT</p>}
                        {saleDetail.amount_change > 0 && <p className="text-emerald-600"><span className="font-semibold">Monnaie rendue : </span>{(saleDetail.amount_change || 0).toFixed(3)} DT</p>}
                        <div className="pt-1">
                          <span className="font-semibold text-gray-700">Statut : </span>
                          <PaymentBadge sale={saleDetail} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer (pinned) */}
                <div className="shrink-0 px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-between rounded-b-2xl">
                  <Link to={`/vente/${saleDetail.id}/receipt`} className="btn-s px-4 py-2 rounded-xl text-sm">
                    <IcFileText /> Générer Reçu
                  </Link>
                  <div className="flex gap-2">
                    {getAmountDue(saleDetail) > 0 && (
                      <button onClick={() => { setShowDetailModal(false); handlePayment(saleDetail); }}
                        className="btn-p px-4 py-2 text-sm gap-2">
                        <IcCreditCard /> Encaisser
                      </button>
                    )}
                    <button onClick={() => setShowDetailModal(false)} className="btn-g px-4 py-2 text-sm gap-2">
                      <IcClose /> Fermer
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerHistorySales;
